export const SCHEMA_VERSION = 3;

export function emptyWorkspace() {
  return { schemaVersion: SCHEMA_VERSION, projects: [], tasks: [], events: [], recurrences: [] };
}

export function normalizeWorkspace(value = {}) {
  const workspace = emptyWorkspace();
  workspace.projects = Array.isArray(value.projects) ? value.projects.map(normalizeProject) : [];
  workspace.events = Array.isArray(value.events) ? value.events.map(normalizeEvent).filter(Boolean) : [];
  workspace.recurrences = Array.isArray(value.recurrences) ? value.recurrences.map(normalizeSeries).filter(Boolean) : [];
  workspace.tasks = Array.isArray(value.tasks) ? value.tasks.map((task) => normalizeTask(task, workspace.events)).filter(Boolean) : [];

  const seriesIds = new Set(workspace.recurrences.map((series) => series.id));
  workspace.tasks.forEach((task) => {
    if (task.recurrenceSeriesId && seriesIds.has(task.recurrenceSeriesId)) return;
    if (task.legacyType !== "routine") {
      task.recurrenceSeriesId = null;
      return;
    }

    const anchorDate = task.plannedDate || task.legacyAnchorDate || localDateKey(new Date());
    const series = normalizeSeries({
      id: makeId(),
      title: task.title,
      projectId: task.projectId,
      details: task.details,
      estimatedMinutes: task.estimatedMinutes,
      color: task.color,
      sourceUrl: task.sourceUrl,
      sourceTitle: task.sourceTitle,
      sourceDomain: task.sourceDomain,
      startDate: anchorDate,
      rule: task.legacyRecurrenceRule,
      isActive: !task.completedAt && !task.skippedAt,
      createdAt: task.createdAt,
    });
    workspace.recurrences.push(series);
    seriesIds.add(series.id);
    task.recurrenceSeriesId = series.id;
  });

  workspace.tasks.forEach((task) => {
    delete task.legacyType;
    delete task.legacyRecurrenceRule;
    delete task.legacyAnchorDate;
  });

  return workspace;
}

export function normalizeRecurrenceRule(rule = {}, anchorDate = localDateKey(new Date())) {
  let frequency = rule.frequency || "weekly";
  if (frequency === "weekdays") frequency = "weekly";
  if (!['daily', 'weekly', 'monthly'].includes(frequency)) frequency = "weekly";

  const anchorWeekday = dateKeyToDate(anchorDate).getDay();
  const legacyWeekdays = rule.frequency === "weekdays" ? [1, 2, 3, 4, 5] : null;
  const weekdays = [...new Set((rule.weekdays || legacyWeekdays || [anchorWeekday]).map(Number))]
    .filter((day) => day >= 0 && day <= 6)
    .sort((a, b) => a - b);

  return {
    frequency,
    interval: 1,
    weekdays: weekdays.length ? weekdays : [anchorWeekday],
    monthDay: clamp(Number(rule.monthDay) || dateKeyToDate(anchorDate).getDate(), 1, 31),
  };
}

export function nextOccurrenceDate(currentDate, rule, endDate = null) {
  if (!currentDate) return null;
  const normalized = normalizeRecurrenceRule(rule, currentDate);
  const current = dateKeyToDate(currentDate);
  let next;

  if (normalized.frequency === "daily") {
    next = addDateDays(current, 1);
  } else if (normalized.frequency === "monthly") {
    const targetMonth = new Date(current.getFullYear(), current.getMonth() + 1, 1, 12);
    const lastDay = new Date(targetMonth.getFullYear(), targetMonth.getMonth() + 1, 0, 12).getDate();
    next = new Date(targetMonth.getFullYear(), targetMonth.getMonth(), Math.min(normalized.monthDay, lastDay), 12);
  } else {
    next = addDateDays(current, 1);
    for (let attempt = 0; attempt < 14 && !normalized.weekdays.includes(next.getDay()); attempt += 1) {
      next = addDateDays(next, 1);
    }
  }

  const key = localDateKey(next);
  return endDate && key > endDate ? null : key;
}

export function localDateKey(value) {
  const date = value instanceof Date ? value : new Date(value);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export function dateKeyToDate(value) {
  const [year, month, day] = String(value).split("-").map(Number);
  return new Date(year, month - 1, day, 12);
}

export function eventDuration(event) {
  return Math.max(0, Math.round((new Date(event.endsAt) - new Date(event.startsAt)) / 60000));
}

export function taskEffectiveDate(task, events = []) {
  const taskEvents = events.filter((event) => event.taskId === task?.id);
  const calendarEvent = taskEvents.find((event) => !event.completedAt) || taskEvents[0];
  return calendarEvent ? localDateKey(calendarEvent.startsAt) : task?.plannedDate || null;
}

export function isTaskOverdue(task, events = [], today = localDateKey(new Date())) {
  if (!task || task.completedAt || task.skippedAt) return false;
  const effectiveDate = taskEffectiveDate(task, events);
  return Boolean(effectiveDate && effectiveDate < today);
}

export function taskOverdueDays(task, events = [], today = localDateKey(new Date())) {
  if (!isTaskOverdue(task, events, today)) return 0;
  return Math.max(1, Math.round((dateKeyToDate(today) - dateKeyToDate(taskEffectiveDate(task, events))) / 86400000));
}

export function deleteProjectFromWorkspace(workspace, projectId) {
  const projectExists = workspace.projects.some((project) => project.id === projectId);
  let taskCount = 0;
  let recurrenceCount = 0;
  workspace.tasks.forEach((task) => {
    if (task.projectId !== projectId) return;
    task.projectId = null;
    taskCount += 1;
  });
  workspace.recurrences.forEach((series) => {
    if (series.projectId !== projectId) return;
    series.projectId = null;
    recurrenceCount += 1;
  });
  workspace.projects = workspace.projects.filter((project) => project.id !== projectId);
  return { deleted: projectExists, taskCount, recurrenceCount };
}

export function snappedTimePlacement(pointerMinute, duration, grabOffsetMinutes = 0, interval = 15, dayMinutes = 1440) {
  const safeInterval = Math.max(1, Math.round(interval) || 15);
  const safeDuration = clamp(Math.round(duration) || safeInterval, safeInterval, dayMinutes);
  const snappedStart = Math.round((pointerMinute - grabOffsetMinutes) / safeInterval) * safeInterval;
  return {
    startMinute: clamp(snappedStart, 0, Math.max(0, dayMinutes - safeDuration)),
    duration: safeDuration,
  };
}

function normalizeProject(project = {}) {
  return {
    id: project.id || makeId(),
    name: String(project.name || "未命名项目"),
    color: project.color || "terracotta",
    sortOrder: Number(project.sortOrder) || 0,
    isArchived: Boolean(project.isArchived),
  };
}

function normalizeTask(task = {}, events = []) {
  if (!task.title) return null;
  const activeEvent = events.find((event) => event.taskId === task.id && !event.completedAt);
  const completedAt = task.completedAt || task.archivedAt || null;
  return {
    id: task.id || makeId(),
    projectId: task.projectId || null,
    recurrenceSeriesId: task.recurrenceSeriesId || null,
    generatedFromTaskId: task.generatedFromTaskId || null,
    title: String(task.title),
    details: String(task.details || ""),
    estimatedMinutes: clamp(Number(task.estimatedMinutes) || 60, 5, 1440),
    plannedDate: task.plannedDate || (activeEvent ? localDateKey(activeEvent.startsAt) : null),
    color: task.color || "terracotta",
    sourceUrl: task.sourceUrl || null,
    sourceTitle: task.sourceTitle || null,
    sourceDomain: task.sourceDomain || null,
    completedAt,
    skippedAt: task.skippedAt || null,
    createdAt: task.createdAt || new Date().toISOString(),
    legacyType: task.type || null,
    legacyRecurrenceRule: task.recurrenceRule || null,
    legacyAnchorDate: task.recurrenceAnchorDate || null,
  };
}

function normalizeEvent(event = {}) {
  if (!event.id || !event.taskId || !event.startsAt || !event.endsAt) return null;
  return {
    id: event.id,
    taskId: event.taskId,
    startsAt: event.startsAt,
    endsAt: event.endsAt,
    completedAt: event.completedAt || null,
  };
}

function normalizeSeries(series = {}) {
  if (!series.id || !series.title) return null;
  const startDate = series.startDate || localDateKey(new Date());
  return {
    id: series.id,
    projectId: series.projectId || null,
    title: String(series.title),
    details: String(series.details || ""),
    estimatedMinutes: clamp(Number(series.estimatedMinutes) || 60, 5, 1440),
    color: series.color || "terracotta",
    sourceUrl: series.sourceUrl || null,
    sourceTitle: series.sourceTitle || null,
    sourceDomain: series.sourceDomain || null,
    startDate,
    endDate: series.endDate || null,
    rule: normalizeRecurrenceRule(series.rule, startDate),
    isActive: series.isActive !== false,
    createdAt: series.createdAt || new Date().toISOString(),
  };
}

function addDateDays(date, count) {
  const next = new Date(date);
  next.setDate(next.getDate() + count);
  return next;
}

function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

function makeId() {
  return globalThis.crypto?.randomUUID?.() || `local-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}
